import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, HostListener, Inject, Input, OnInit, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import * as moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { STORAGE } from 'src/app/core/services/app-storage.service';
import { AppointmentService } from 'src/app/core/services/appointment.service';
import { BaseService } from 'src/app/core/services/base.service';
import { IframeMessageService } from 'src/app/core/services/iframe-message.service';
import { ReferralMedicalService } from 'src/app/core/services/referral-medical.service';
import { RouteDialogService } from 'src/app/core/services/route-dialog.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { MapDialogComponent } from 'src/app/shared/components/dialogs/map-dialog/map-dialog.component';
import { AppointmentRouteDialogModel } from 'src/app/shared/components/dialogs/route-dialog/route-dialog.component';
import { DialogData, MapDialogData } from 'src/app/shared/models/dialog-data.model';
import { LocationPublicProfile, PublicProfile } from 'src/app/shared/models/people/public-profile.model';
import { ScheduleByLocation } from 'src/app/shared/models/process/schedule-by-location.model';
import { AppointmentCheckout } from 'src/app/shared/models/workflow/models/appointment-checkout.model';

@Component({
  selector: 'app-appointment-location',
  templateUrl: './appointment-location.component.html',
  styleUrls: ['./appointment-location.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class AppointmentLocationComponent implements OnInit {

  private MAX_NUMBER_OF_ITEM_VISIBLE_DEFAULT:number = 4;
  private ngUnsubscribe = new Subject();

  @Input() location: LocationPublicProfile;
  @Input() publicProfile: PublicProfile;
  @Input() selectedLocationId: number;

  numberOfDays: number;
  loading: boolean;
  
  appointmentLocation: AppointmentLocationDto;
  language: string;
  private doGetAppointmentsScheduleSubscribe:Subscription;
  private doGetAppointmentsScheduleNextItemSubscribe:Subscription;

  constructor(
    private baseService: BaseService,
    private utilsService: UtilsService,
    private appointmentService: AppointmentService,
    private routeDialogService: RouteDialogService,
    private dialog: MatDialog,
    private referralMedicalService: ReferralMedicalService,
    private iframeMessageService: IframeMessageService,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.language = locale.dateLanguage ?? 'es-AR';
  }
  
  ngOnInit(): void {
    this.loading = true;
    this.numberOfDays = this.getSchedulerAmountOfDays();

    this.initAppointmentLocationObject();

    if (this.location.idPerfilPublicoDetalle == this.selectedLocationId) {
      this.appointmentLocation.showMap = true;

      setTimeout(() => {
        let container = document.body.classList.contains('route-dialog-opened') 
          ? this.document.getElementsByClassName("route-dialog-content")[0]
          : this.document.getElementsByClassName("body-container")[0];

        let el = this.document.getElementById(this.location.idPerfilPublicoDetalle.toString()) as Element;        

        this.utilsService.scrollToElementWithAnimation(container, el);
      }, 100);
    }    
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initAppointmentLocationObject(){
    this.appointmentLocation = new AppointmentLocationDto();

    this.appointmentLocation.name = this.getLocationName();
    this.appointmentLocation.address = this.getLocationAddress();
    this.appointmentLocation.contacts = this.getLocationContacts();

    this.appointmentLocation.online = this.location.localidad.citaEnLineaHabilitada == 'S';
    this.appointmentLocation.virtual = this.location.localidad.teleconsulta == 'S';
    this.appointmentLocation.additionalInformation = this.location.localidad.informacionTeleconsulta;

    this.appointmentLocation.numberOfDays = this.numberOfDays;

    if(this.location.localidad.coordenadas){
      let coords = this.location.localidad.coordenadas.split(';');

      this.appointmentLocation.lat = +coords[0];
      this.appointmentLocation.lng = +coords[1];

      if(this.location.localidad.teleconsulta == 'S')
        this.appointmentLocation.mapDisabled = true;
    }
    else{
      this.appointmentLocation.mapDisabled = true;
    }

    this.getAttentionSchedule();
  }

  onClickShowMap(){
    let data = new MapDialogData();
    
    data.title = this.appointmentLocation.name;
    // data.description = this.appointmentLocation.address;
    data.showWazeButton = true;
    data.message = `${this.appointmentLocation.lat};${this.appointmentLocation.lng}`;
    data.animationIn = 'map-phr-animation-in';
    data.animationOut = 'map-phr-animation-out';
    data.showCloseTextButton = true;

    this.dialog.open(MapDialogComponent, {          
      panelClass: ['map-dialog','map-phr-dialog', 'map-phr-animation-in'],
      data: data
    });
  }

  getScheduleColumnStyle():string {
    return  this.appointmentLocation.nextAvailableAppointmentDate
      ? "repeat(1, 1fr)" 
      : "repeat(" + this.appointmentLocation.schedules.length + ", 1fr)";
  }

  getSkeletonColumnStyle():string{
    return "repeat(" + (this.numberOfDays + 1) + ", 1fr)";
  }

  onClickBtnPrevious(){
    let now = new Date();

    this.appointmentLocation.startDate = new Date(this.appointmentLocation.startDate.getTime() - ((this.appointmentLocation.numberOfDays + 1) * 24 * 60 * 60 * 1000));

    if (this.appointmentLocation.startDate < now)
        this.appointmentLocation.startDate = now;

    this.getAttentionSchedule();
  }

  onClickBtnNext(){
    this.appointmentLocation.startDate = new Date(this.appointmentLocation.endDate.getTime() + (24 * 60 * 60 * 1000));

    this.getAttentionSchedule();
  }

  onClickBtnCollapse(){
    this.appointmentLocation.collapseOpened = !this.appointmentLocation.collapseOpened;
  }

  showViewMoreText(item:ScheduleItemDto){
    
    let datesFiltered = item.dates.filter(d => d.enabled);

    return !this.appointmentLocation.collapseOpened && datesFiltered.length > this.MAX_NUMBER_OF_ITEM_VISIBLE_DEFAULT;
  }

  showViewLessText(item:ScheduleItemDto){
    
    let datesFiltered = item.dates.filter(d => d.enabled);

    return this.appointmentLocation.collapseOpened && datesFiltered.length > this.MAX_NUMBER_OF_ITEM_VISIBLE_DEFAULT;
  }

  moveScheduleToAvailableAppointment(){

    var _availableDate = this.appointmentLocation.nextAvailableAppointmentDate ?? new Date();

    let now = new Date();

    var hr = _availableDate.getHours();
    var min = _availableDate.getMinutes();

    // Remove hr and min
    _availableDate = new Date(_availableDate.getFullYear(), _availableDate.getMonth(), _availableDate.getDate());

    // We cannot move directly to that date because we can change the current date interval
    // So we need to increase the date interval until the next date is inside it
    var reached = false;
    var startDate = new Date(this.appointmentLocation.startDate.getFullYear(), this.appointmentLocation.startDate.getMonth(), this.appointmentLocation.startDate.getDate());
    var endDate = new Date(this.appointmentLocation.endDate.getFullYear(), this.appointmentLocation.endDate.getMonth(), this.appointmentLocation.endDate.getDate());
    var iteration = 0;

    while (!reached && iteration < 1000) {
        iteration++;

        startDate = new Date(endDate.getTime() + (24 * 60 * 60 * 1000));
        endDate = new Date(startDate.getTime() + (this.appointmentLocation.numberOfDays * 24 * 60 * 60 * 1000));

        reached = startDate <= _availableDate && _availableDate <= endDate;
    }

    // Set start date
    if (iteration < 1000) {
        this.appointmentLocation.startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), hr, min, 0);

        if (this.appointmentLocation.startDate < now)
            this.appointmentLocation.startDate = now;

        this.getAttentionSchedule();
    }
    else {
        console.error("Error navigating to next available appointment");
    }

  }

  gotoCheckout(dateItem:ScheduleDateItemDto){
    let dto = new AppointmentCheckout();

    dto.fechaInicio = dateItem.date || new Date();
    dto.fechaFin = moment(dto.fechaInicio).add(dateItem.duration || 30, 'm').toDate();
    dto.idLocalidad = this.location.localidad.id;
    dto.nombreLocalidad = this.location.localidad.nombre;
    dto.direccionLocalidad = this.location.localidad.direccion1;
    dto.coordenadasLocalidad = this.location.localidad.coordenadas;
    dto.telefonoLocalidad = this.location.localidad.tel 
      ? this.location.localidad.tel 
      : this.location.localidad.tel2;
    dto.idResponsableServicio = this.publicProfile.id;
    dto.nombreResponsableServicio = this.publicProfile.nombrePersona;
    dto.urlAvatarResponsableServicio = this.publicProfile.ubicacionFoto;
    dto.nickname = this.publicProfile.username;
    dto.especialidadResponsableServicio = this.publicProfile.especialidad;
    dto.idEmpresa = this.location.idEmpresa ?? this.publicProfile.idEmpresa;
    dto.teleconsulta = this.location.localidad.teleconsulta;
    
    dto.perfilPublico = this.publicProfile;

    let routeDialogModel = new AppointmentRouteDialogModel();
    routeDialogModel.state = dto;

    if(this.referralMedicalService.isReferralMedical()){
      let referralMedical = this.referralMedicalService.getReferralMedical();
      referralMedical.data = dto;
      
      this.iframeMessageService.parentPostMessage(STORAGE.REFERRAL_MEDICAL, referralMedical);
    }
    else{
      this.routeDialogService.openDialog(routeDialogModel);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {

    let newNumberOfDays = this.getSchedulerAmountOfDays();

    if(newNumberOfDays == this.numberOfDays){
      return;
    }

    this.numberOfDays = newNumberOfDays;
    this.appointmentLocation.numberOfDays = this.numberOfDays;
    this.getAttentionSchedule();
  }

  private getLocationName(): string {
    return this.location.localidad.nombreLocalidadPerfilLugarAtencion 
      ? this.location.localidad.nombreLocalidadPerfilLugarAtencion
      : this.location.localidad.nombre;
  }

  private getLocationAddress(): string{
    var result = '';

    if (this.location.localidad.direccion2)
        result += this.location.localidad.direccion2;

    if (this.location.localidad.direccion2 && this.location.localidad.direccion1)
        result += ' - ';

    if (this.location.localidad.direccion1) {
        result += this.location.localidad.direccion1;
    }

    if (this.location.localidad.nombreCanonicoDivisionTerritorial)
        result += " " + this.location.localidad.nombreCanonicoDivisionTerritorial;

    return result;
  }

  private getLocationContacts(){
    let result = '';

    if (this.location.localidad.tel)
        result += this.location.localidad.tel;

    if (this.location.localidad.tel && this.location.localidad.tel2)
        result += " • ";

    if (this.location.localidad.tel2)
        result += this.location.localidad.tel2;

    if (this.location.localidad.email)
        result += (result ? " • " + this.location.localidad.email : this.location.localidad.email);

    return result;
  }

  private getAttentionSchedule(){

    let endDate = this.appointmentLocation.startDate.getTime();
    this.appointmentLocation.endDate = new Date(endDate + (this.appointmentLocation.numberOfDays * 24 * 60 * 60 * 1000));

    this.goGetAppointmentsSchedule();
  }

  private goGetAppointmentsSchedule(){

    this.appointmentLocation.loading = true;

    if(this.doGetAppointmentsScheduleSubscribe){
      this.doGetAppointmentsScheduleSubscribe.unsubscribe();
    }

    let _idEmpresa = this.publicProfile.idEmpresa;
    let _idResponsableServicio = this.publicProfile.idPerfilPublico == this.location.localidad.idPersona 
      ? this.publicProfile.idPerfilPublico      
      : this.location.localidad.idPersona;
    let _fechaInicio = this.getDateStringFormat(this.appointmentLocation.startDate);
    let _fechaFin = this.getDateStringFormat(this.appointmentLocation.endDate);
    let _idLocalidad = this.location.localidad.id;

    this.doGetAppointmentsScheduleSubscribe = this.appointmentService
    .getAppointmentsSchedule(_idEmpresa, _idResponsableServicio, _fechaInicio, _fechaFin, _idLocalidad)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(result => {

      this.processAppointnmentsSchedule(result);

      this.appointmentLocation.loading = false;
      this.loading = false;
    },
    error => {
      this.baseService.handleServiceError(error, "Error getting appointments schedule");      
    });
  }

  private processAppointnmentsSchedule(appointmentsSchedule:ScheduleByLocation[]){
    this.appointmentLocation.nextAvailableAppointmentDate = undefined;

    let _appointmentSchedule = appointmentsSchedule.find(as=> as.idLocalidad == this.location.localidad.id);

    this.appointmentLocation.schedules = [];
    moment.locale('en'); // Es necesario ya que en datepicker-flat-field cambia el locale a es (si el idioma es español)
    for (var j = 0; j < this.appointmentLocation.numberOfDays + 1; j++){
      var date = new Date(new Date(this.appointmentLocation.startDate.getTime()).setDate(this.appointmentLocation.startDate.getDate() + j));

      var scheduleItem = new ScheduleItemDto();

      scheduleItem.day = date.getDate();
      scheduleItem.dayName = `WeekDayShort${moment(date).format('ddd')}`;
      scheduleItem.monthName = `MonthsShort${moment(date).format('MMM')}`;
      scheduleItem.dates = [];

      this.appointmentLocation.schedules.push(scheduleItem);
    }

    if(_appointmentSchedule && _appointmentSchedule.turnos.length > 0){
      
      _appointmentSchedule.turnos.forEach(turno => {

        let utcDate = moment.utc(turno.fechaHoraInicio).toDate();

        turno.fechaHoraInicio = utcDate;

        let day = turno.fechaHoraInicio.getUTCDate();

        // Check if item already exist for this day
        let scheduleItem = this.appointmentLocation.schedules.find(i => i.day === day);

        if (scheduleItem) {
            // Add date to item
            scheduleItem.dates.push({
                date: turno.fechaHoraInicio,
                duration: turno.duracionEnMinutos,
                enabled: true
            });

            // If at least one of this schedulers has more than 4 items let show the collapsable
            if (scheduleItem.dates.length > this.MAX_NUMBER_OF_ITEM_VISIBLE_DEFAULT)
                this.appointmentLocation.showCollapsable = true;
        }
        else{
          console.error(turno.fechaHoraInicio);
        }
      });

      let max = Math.max.apply(null, this.appointmentLocation.schedules.map(a => a.dates.length));

      this.appointmentLocation.schedules.forEach(_scheduleItem =>{
        for (var k = _scheduleItem.dates.length; k < max; k++) {
          _scheduleItem.dates.push(new ScheduleDateItemDto());
        }
      });
    }
    else{

      this.getNextAvailableAppointmentSchedule();
    }

    this.appointmentLocation.btnPreviousEnabled = (new Date() < this.appointmentLocation.startDate);
  }

  private getNextAvailableAppointmentSchedule(){

    this.appointmentLocation.loading = true;

    if(this.doGetAppointmentsScheduleNextItemSubscribe){
      this.doGetAppointmentsScheduleNextItemSubscribe.unsubscribe();
    }

    let _idEmpresa = this.publicProfile.idEmpresa;
    let _idResponsableServicio = this.publicProfile.idPerfilPublico == this.location.localidad.idPersona 
      ? this.publicProfile.idPerfilPublico      
      : this.location.localidad.idPersona;
    let _fechaInicio = this.getDateStringFormat(this.appointmentLocation.startDate);
    let _idLocalidad = this.location.localidad.id;

    this.doGetAppointmentsScheduleNextItemSubscribe = this.appointmentService
      .getAppointmentsScheduleNextItem(_idEmpresa, _idResponsableServicio, _fechaInicio, _idLocalidad)
      .subscribe(result => {
        if (result) {
          this.appointmentLocation.nextAvailableAppointmentDate = moment.utc(result).toDate();
          this.appointmentLocation.loading = false;
        }
        else {
          this.appointmentLocation.loading = false;
          this.appointmentLocation.online = false;
          this.appointmentLocation.nextAvailableAppointmentDate = undefined;
        }        
      }, error =>{
        this.appointmentLocation.loading = false;
        this.appointmentLocation.online = false;
        this.appointmentLocation.nextAvailableAppointmentDate = undefined;
      });

  }

  private getDateStringFormat(date: Date){
    return moment([date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0]).format("YYYY-MM-DD");
  }

  private getSchedulerAmountOfDays() {
    var days = 6;

    if(isPlatformBrowser(this.platformId)){
      var width = window.innerWidth;
      if (width <= 440)
          days = 2;
      else if (width <= 600)
          days = 3;
      else if (width <= 800)
          days = 4;
      else if (width < 900)
          days = 5;
    }

    return days;
  }
}

export class AppointmentLocationDto{

  name: string;
  additionalInformation: string;
  address: string;
  contacts: string;

  online:boolean;
  loading:boolean;
  virtual: boolean;
  startDate:Date = new Date();
  endDate:Date;
  btnPreviousEnabled:boolean;

  numberOfDays: number;
  schedules:ScheduleItemDto[];

  lat:number;
  lng:number;
  zoom: number = 15;

  showMap: boolean;
  mapDisabled:boolean;

  showCollapsable: boolean = false;
  collapseOpened: boolean = false;

  nextAvailableAppointmentDate?: Date;
}

export class ScheduleItemDto{
  day: number;
  dayName: string;
  monthName: string;
  dates:ScheduleDateItemDto[];
}

export class ScheduleDateItemDto{
  date?: Date;
  duration?: number;
  enabled: boolean;
} 